/**
 * L1-FE-03 — Upload history (cross-batch table).
 *
 * Filterable by type + period (yyyy-mm) + status. The BE's GET /uploads has no
 * cursor — it returns the newest `limit` rows (max 500), so this is a single
 * bounded query (no "Load more"); a footer note appears if the cap is hit.
 * Row click opens the upload detail page.
 */

import {
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronRight,
  IconCloudUpload,
  IconHistory,
} from "@tabler/icons-react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type UploadHistoryFilter, useBatches, useUploadHistory } from "../api/hooks";
import type { ManualUpload, UploadStatus, UploadType } from "../api/types";
import { useUploadTypeMeta } from "../auth/useBranding";
import { formatPacificDisplay } from "../lib/datetime";
import { formatApiError } from "../lib/errors";
import { UPLOAD_STATUS_COLOR, UPLOAD_TYPE_ORDER } from "../lib/uploadTypes";
import { UploadsTabs } from "./uploads/UploadsTabs";

// Compact labels come from the shared registry, so a new upload type shows up
// here the moment it is added there instead of silently filtering as "". Read
// through the hook: the custody types are named for the acting organization.
function useTypeLabels(): Record<UploadType, string> {
  const meta = useUploadTypeMeta();
  return useMemo(
    () =>
      Object.fromEntries(UPLOAD_TYPE_ORDER.map((type) => [type, meta[type].shortLabel])) as Record<
        UploadType,
        string
      >,
    [meta],
  );
}

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "received", label: "Received" },
  { value: "parsed", label: "Parsed" },
  { value: "accepted", label: "Accepted" },
  { value: "filed", label: "Filed" },
  { value: "rejected", label: "Rejected" },
  { value: "superseded", label: "Superseded" },
];

export function UploadHistoryPage() {
  const typeLabel = useTypeLabels();
  const typeOptions = useMemo(
    () => [
      { value: "", label: "All types" },
      ...UPLOAD_TYPE_ORDER.map((type) => ({ value: type, label: typeLabel[type] })),
    ],
    [typeLabel],
  );
  const batchesQ = useBatches();
  const periods = useMemo(() => {
    const set = new Set((batchesQ.data?.items ?? []).map((b) => b.period_start.slice(0, 7)));
    return Array.from(set).sort().reverse();
  }, [batchesQ.data]);

  // Filters are state-of-record in the URL (locked convention) so a filtered
  // history view survives reload and is shareable. Empty string = "all".
  const [params, setParams] = useSearchParams();
  const type = params.get("type") ?? "";
  const status = params.get("status") ?? "";
  const period = params.get("period") ?? "";
  const setFilter = (key: "type" | "status" | "period", value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  const filter: UploadHistoryFilter = {
    type: (type || undefined) as UploadType | undefined,
    status: (status || undefined) as UploadStatus | undefined,
    period: period || undefined,
  };

  const historyQ = useUploadHistory(filter);
  const items = historyQ.data?.items ?? [];

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group justify="space-between" wrap="wrap" gap="md">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="indigo" size={36} radius="md">
              <IconHistory size={20} />
            </ThemeIcon>
            <Title order={2}>Upload history</Title>
          </Group>
          <UploadsTabs />
        </Group>
        <Text c="dimmed" size="sm" maw={720}>
          Every upload, across all batches, newest first (most recent 500). Filters narrow the list
          server-side by reporting period, type, and status.
        </Text>
      </Stack>

      <Paper p="md" radius="md" withBorder>
        <Group gap="md" wrap="wrap">
          <Select
            label="Type"
            data={typeOptions}
            value={type}
            onChange={(v) => setFilter("type", v ?? "")}
            w={200}
            clearable={false}
            size="sm"
          />
          <Select
            label="Period"
            data={[
              { value: "", label: "All periods" },
              ...periods.map((p) => ({ value: p, label: p })),
            ]}
            value={period}
            onChange={(v) => setFilter("period", v ?? "")}
            w={180}
            clearable={false}
            size="sm"
            disabled={batchesQ.isPending}
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setFilter("status", v ?? "")}
            w={200}
            clearable={false}
            size="sm"
          />
        </Group>
      </Paper>

      {historyQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">{formatApiError(historyQ.error)}</Text>
        </Alert>
      ) : null}

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table
            verticalSpacing="sm"
            horizontalSpacing="md"
            fz="sm"
            highlightOnHover
            stickyHeader
            miw={920}
          >
            <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
              <Table.Tr>
                <Table.Th style={{ width: 220 }}>Filename</Table.Th>
                <Table.Th style={{ width: 140 }}>Type</Table.Th>
                <Table.Th style={{ width: 110 }}>Period</Table.Th>
                <Table.Th style={{ width: 200 }}>Batch</Table.Th>
                <Table.Th style={{ width: 120 }}>Status</Table.Th>
                <Table.Th style={{ width: 200 }}>Uploaded</Table.Th>
                <Table.Th style={{ width: 28 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((u) => (
                <UploadRow key={u.id} upload={u} />
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {historyQ.isPending ? (
          <Box p="md">
            <Group gap="sm" justify="center">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading uploads…
              </Text>
            </Group>
          </Box>
        ) : items.length === 0 ? (
          <Box p="xl">
            <Stack align="center" gap="xs">
              <ThemeIcon variant="light" color="gray" radius="xl" size={40}>
                <IconCloudUpload size={20} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">
                No uploads match the current filter.
              </Text>
            </Stack>
          </Box>
        ) : null}
      </Paper>

      {items.length >= 500 ? (
        <Text size="xs" c="dimmed" ta="center">
          Showing the most recent 500 uploads. Narrow the filters to see older records.
        </Text>
      ) : null}
    </Stack>
  );
}

function UploadRow({ upload }: { upload: ManualUpload }) {
  const navigate = useNavigate();
  const typeLabel = useTypeLabels();
  return (
    <Table.Tr style={{ cursor: "pointer" }} onClick={() => navigate(`/uploads/${upload.id}`)}>
      <Table.Td>
        <Text size="sm" fw={500} truncate>
          {upload.original_filename}
        </Text>
        <Text size="xs" c="dimmed" ff="monospace">
          {upload.id}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="default" size="sm">
          {typeLabel[upload.upload_type]}
        </Badge>
      </Table.Td>
      <Table.Td ff="monospace" fz="xs">
        {upload.period}
      </Table.Td>
      <Table.Td ff="monospace" fz="xs" c={upload.batch_id ? undefined : "dimmed"}>
        {upload.batch_id ?? "—"}
      </Table.Td>
      <Table.Td>
        <Badge color={UPLOAD_STATUS_COLOR[upload.status]} variant="light" size="sm">
          {upload.status}
        </Badge>
      </Table.Td>
      <Table.Td fz="xs" c="dimmed">
        {formatPacificDisplay(upload.uploaded_at)}
      </Table.Td>
      <Table.Td>
        <IconChevronRight size={14} style={{ color: "var(--mantine-color-gray-5)" }} />
      </Table.Td>
    </Table.Tr>
  );
}
